import { Button, Switch } from '@material-ui/core';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { URL_ESTOY_SIN_LUZ } from 'navigation';
import AnalyticsService from 'services/AnalyticsService';
import { actualizarSwitchCortesFuturos, RootState } from 'store';
import { EventosGtmSaesa } from 'utils/GtmEvents';
import { flashSlash } from 'assets/images';
import clsx from 'clsx';
import useStyles from './HeaderMapaStyles';

const HeaderMapa = () => {
  const styles = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const logo = useSelector((state: RootState) => state.desconexion.distribuidora);
  const cortesFuturosHabilitado = useSelector((state: RootState) => state.desconexion.cortesFuturos);
  const switchCortesFuturos = useSelector((state: RootState) => state.desconexion.switchCortesFuturos);
  const [panelMobileAbierto, setPanelMobileAbierto] = useState(false);
  const fabSimbologiaRef = useRef<HTMLButtonElement>(null);

  const abrirEstoySinLuz = () => {
    window.open(`${URL_ESTOY_SIN_LUZ}`, '_blank', 'noopener,noreferrer');
    AnalyticsService.enviarTag(EventosGtmSaesa.mapaDesconexiones.reportarCorte);
  };

  const onSwitchCortesFuturosChange = () => {
    dispatch(actualizarSwitchCortesFuturos(!switchCortesFuturos));
    AnalyticsService.enviarTag(EventosGtmSaesa.mapaDesconexiones.poligono.switchCortesFuturos);
  };

  useEffect(() => {
    if (!panelMobileAbierto) {
      return undefined;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPanelMobileAbierto(false);
        if (fabSimbologiaRef.current) {
          fabSimbologiaRef.current.focus();
        }
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [panelMobileAbierto]);

  const legendItems = (
    <>
      <div className={styles.legendGrupo}>
        <span className={styles.legendTitulo}>{t('desconexion:paginas.mapaCortes.tipoCortes.title')}</span>
        <div className={styles.legendItem}>
          <span className={clsx(styles.legendDot, styles.dotInesperado)} />
          <span className={styles.legendTexto}>{t('desconexion:paginas.mapaCortes.tipoCortes.item1')}</span>
        </div>
        <div className={styles.legendItem}>
          <span className={clsx(styles.legendDot, styles.dotProgramado)} />
          <span className={styles.legendTexto}>{t('desconexion:paginas.mapaCortes.tipoCortes.item2')}</span>
        </div>
      </div>
      {cortesFuturosHabilitado && (
        <>
          <div className={styles.separador} />
          <div className={styles.futurosCnt}>
            <div className={styles.legendItem}>
              <span className={clsx(styles.legendDot, styles.dotFuturos)} />
              <span className={clsx(styles.legendTexto, { [styles.legendTextoMuted]: !switchCortesFuturos })}>
                {t('desconexion:paginas.mapaCortes.tipoCortes.item3')}
              </span>
            </div>
            <Switch
              checked={switchCortesFuturos}
              onChange={onSwitchCortesFuturosChange}
              color="primary"
              inputProps={{ 'aria-label': 'switch-cortes-futuros' }}
              data-testid="switch-cortes-futuros"
            />
          </div>
        </>
      )}
    </>
  );

  return (
    <>
      <div className={styles.root}>
        <div className={styles.barra}>
          <Link className={styles.logoLink} to="/" data-testid="header-mapa-logo">
            <img className={styles.logo} src={logo?.urlLogo} alt="saesa" />
          </Link>
          <div className={styles.legendCnt}>{legendItems}</div>
          <Button className={styles.botonReportar} onClick={abrirEstoySinLuz} data-testid="boton-reportar-corte">
            {t('header.botonSinLuz')}
          </Button>
        </div>
      </div>

      <div className={styles.fabsMobile}>
        <button
          type="button"
          className={styles.fab}
          onClick={abrirEstoySinLuz}
          aria-label={t('header.botonSinLuz')}
          data-testid="fab-reportar-corte"
        >
          <img src={flashSlash} alt="" width={24} height={24} />
        </button>
        <button
          type="button"
          className={styles.fab}
          ref={fabSimbologiaRef}
          onClick={() => setPanelMobileAbierto(prev => !prev)}
          aria-label={t('desconexion:paginas.mapaCortes.select.label')}
          aria-expanded={panelMobileAbierto}
          aria-controls="panel-simbologia-mobile"
          data-testid="fab-simbologia"
        >
          <span className={styles.dotsLegend}>
            <span className={clsx(styles.legendDot, styles.dotInesperado)} />
            <span className={clsx(styles.legendDot, styles.dotProgramado)} />
            {cortesFuturosHabilitado && <span className={clsx(styles.legendDot, styles.dotFuturos)} />}
          </span>
        </button>
      </div>

      {panelMobileAbierto && (
        <div
          className={styles.panelMobile}
          id="panel-simbologia-mobile"
          data-testid="panel-simbologia-mobile"
          role="dialog"
          aria-modal="true"
          aria-label={t('desconexion:paginas.mapaCortes.tipoCortes.title')}
        >
          <div className={styles.panelMobileRow}>{legendItems}</div>
          <Button className={styles.botonReportarMobile} onClick={abrirEstoySinLuz}>
            {t('header.botonSinLuz')}
          </Button>
        </div>
      )}
    </>
  );
};

export default HeaderMapa;
